import React, { useEffect, useMemo, useState } from 'react';
import ChipRows from './ChipRows';
import ProjectCard from './ProjectCard';
import SectionHeader from './SectionHeader';
import WritingStrip from './WritingStrip';

// Curated flagship works shown as editorial rows above the archive grid.
// (Pokémon TCG simulator is intentionally NOT featured — it scrapes official
// card images and should stay low-profile; it remains in the archive.)
const FEATURED_IDS = [8, 10];
const FILTER_CHIP_LIMIT = 8;

function WorksSection({ projects, language }) {
  const [projectSort, setProjectSort] = useState('newest');
  const [activeTag, setActiveTag] = useState('');

  const tagsKey = language === 'ja' ? 'tags_ja' : 'tags_en';

  const tagCounts = useMemo(() => {
    const counts = new Map();

    projects.forEach((project) => {
      (project[tagsKey] || []).forEach((tag) => {
        counts.set(tag, (counts.get(tag) || 0) + 1);
      });
    });

    return counts;
  }, [projects, tagsKey]);

  const topTags = useMemo(
    () =>
      [...tagCounts.entries()]
        .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
        .slice(0, FILTER_CHIP_LIMIT)
        .map(([tag]) => tag),
    [tagCounts]
  );

  useEffect(() => {
    if (activeTag && !tagCounts.has(activeTag)) {
      setActiveTag('');
    }
  }, [activeTag, tagCounts]);

  const featuredProjects = useMemo(
    () => FEATURED_IDS.map((id) => projects.find((project) => project.id === id)).filter(Boolean),
    [projects]
  );

  const orderedProjects = useMemo(() => {
    const normalizeId = (project) => (typeof project.id === 'number' ? project.id : Number.NaN);

    return [...projects]
      .filter((project) => !activeTag || (project[tagsKey] || []).includes(activeTag))
      .sort((left, right) => {
        const leftId = normalizeId(left);
        const rightId = normalizeId(right);
        const leftIsValid = Number.isFinite(leftId);
        const rightIsValid = Number.isFinite(rightId);

        if (!leftIsValid && !rightIsValid) {
          return 0;
        }
        if (!leftIsValid) {
          return 1;
        }
        if (!rightIsValid) {
          return -1;
        }

        return projectSort === 'newest' ? rightId - leftId : leftId - rightId;
      });
  }, [activeTag, projectSort, projects, tagsKey]);

  const copy =
    language === 'ja'
      ? {
          label: 'SELECTED WORK',
          title: '作品',
          lead: '設計から実装、公開後の運用まで。手を動かして届けてきたプロダクトです。',
          archiveLabel: 'ARCHIVE',
          archiveTitle: '開発プロジェクト',
          sortLabel: '並び順',
          newest: '新しい順',
          oldest: '古い順',
          filterLabel: 'タグ絞り込み',
          all: '全て',
          hint: 'タグをクリックすると絞り込めます',
          result: activeTag
            ? `${activeTag}：${orderedProjects.length}件`
            : `全${orderedProjects.length}件`,
          empty: '該当するプロジェクトがありません。',
        }
      : {
          label: 'SELECTED WORK',
          title: 'Works',
          lead: 'Products carried from design through implementation to production operations.',
          archiveLabel: 'ARCHIVE',
          archiveTitle: 'Software projects',
          sortLabel: 'Sort',
          newest: 'Newest',
          oldest: 'Oldest',
          filterLabel: 'Filter by tag',
          all: 'All',
          hint: 'Click any tag to filter',
          result: activeTag
            ? `${activeTag}: ${orderedProjects.length} shown`
            : `${orderedProjects.length} projects`,
          empty: 'No projects match this tag.',
        };

  const handleTagClick = (tag) => {
    setActiveTag((prevTag) => (prevTag === tag ? '' : tag));
  };

  return (
    <section id="works" className="works-section" aria-label={copy.title}>
      <SectionHeader index="05" label={copy.label} title={copy.title} lead={copy.lead} />

      <div className="works-featured">
        {featuredProjects.map((project, index) => (
          <ProjectCard
            key={project.id}
            project={project}
            language={language}
            index={project.id}
            variant="featured"
            flip={index % 2 === 1}
            activeTag={activeTag}
            onTagClick={handleTagClick}
          />
        ))}
      </div>

      <WritingStrip language={language} />

      {/* No reveal wrapper here: the archive is taller than the viewport, so a
          single IntersectionObserver threshold could leave it stuck invisible. */}
      <div id="archive" className="works-archive">
        <div className="works-archive__head">
          <p className="section-head__index mono">07 — {copy.archiveLabel}</p>
          <h3 className="works-archive__title">{copy.archiveTitle}</h3>
        </div>

        <div className="works-controls">
          <div className="works-controls__sort" role="group" aria-label={copy.sortLabel}>
            <button
              type="button"
              className={`chip ${projectSort === 'newest' ? 'is-active' : ''}`}
              onClick={() => setProjectSort('newest')}
              aria-pressed={projectSort === 'newest'}
            >
              {copy.newest}
            </button>
            <button
              type="button"
              className={`chip ${projectSort === 'oldest' ? 'is-active' : ''}`}
              onClick={() => setProjectSort('oldest')}
              aria-pressed={projectSort === 'oldest'}
            >
              {copy.oldest}
            </button>
          </div>

          <ChipRows
            className="works-controls__filter"
            ariaLabel={copy.filterLabel}
            language={language}
            activeKey={activeTag}
          >
            <button
              type="button"
              className={`chip ${activeTag ? '' : 'is-active'}`}
              onClick={() => setActiveTag('')}
              aria-pressed={!activeTag}
            >
              {copy.all}
            </button>
            {topTags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`chip ${activeTag === tag ? 'is-active' : ''}`}
                onClick={() => handleTagClick(tag)}
                aria-pressed={activeTag === tag}
              >
                {tag}
                <span className="chip__count">{tagCounts.get(tag)}</span>
              </button>
            ))}
            {activeTag && !topTags.includes(activeTag) ? (
              <button
                type="button"
                className="chip is-active"
                onClick={() => handleTagClick(activeTag)}
                aria-pressed="true"
              >
                {activeTag}
                <span className="chip__count">{tagCounts.get(activeTag)}</span>
              </button>
            ) : null}
          </ChipRows>
        </div>

        <p className="works-controls__result mono" role="status">
          {copy.result}
          {!activeTag ? <span className="works-controls__hint"> · {copy.hint}</span> : null}
        </p>

        <div className="works-grid" key={`${activeTag}-${projectSort}`}>
          {orderedProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              language={language}
              index={project.id}
              variant="compact"
              activeTag={activeTag}
              onTagClick={handleTagClick}
            />
          ))}
        </div>

        {orderedProjects.length === 0 ? <p className="works-grid__empty">{copy.empty}</p> : null}
      </div>
    </section>
  );
}

export default WorksSection;
